/**
 * API Configuration for Demo Application
 *
 * This is a modified version that uses the mock API instead of real backend calls.
 */

import MockAPI from '../mockApi';

// Mock API base URL (not actually used, but kept for compatibility)
export const API_BASE_URL = 'http://demo.local';

// For compatibility with existing code, these endpoints are defined but not used
export const API_ENDPOINTS = {
  auth: {
    register: `${API_BASE_URL}/api/auth/register`,
    login: `${API_BASE_URL}/api/auth/login`,
    logout: `${API_BASE_URL}/api/auth/logout`,
    verifyEmail: `${API_BASE_URL}/api/auth/verify-email`,
    resendVerification: `${API_BASE_URL}/api/auth/resend-verification`,
    forgotPassword: `${API_BASE_URL}/api/auth/forgot-password`,
    resetPassword: `${API_BASE_URL}/api/auth/reset-password`,
    changePassword: `${API_BASE_URL}/api/auth/change-password`,
    me: `${API_BASE_URL}/api/auth/me`,
  },
  users: {
    profile: `${API_BASE_URL}/api/users/profile`,
    updateProfile: `${API_BASE_URL}/api/users/profile`,
    updateEmail: `${API_BASE_URL}/api/users/email`,
    updatePassword: `${API_BASE_URL}/api/users/password`,
  },
  manuscripts: {
    list: `${API_BASE_URL}/api/manuscripts`,
    create: `${API_BASE_URL}/api/manuscripts`,
    detail: (id: string) => `${API_BASE_URL}/api/manuscripts/${id}`,
    update: (id: string) => `${API_BASE_URL}/api/manuscripts/${id}`,
    delete: (id: string) => `${API_BASE_URL}/api/manuscripts/${id}`,
  },
  betaApplications: {
    submit: `${API_BASE_URL}/api/beta-applications`,
    myApplications: `${API_BASE_URL}/api/beta-applications/my-applications`,
    myActiveReads: `${API_BASE_URL}/api/beta-applications/my-active-reads`,
    activeReaders: `${API_BASE_URL}/api/beta-applications/active-readers`,
    allApplications: `${API_BASE_URL}/api/beta-applications/all-applications`,
    forManuscript: (manuscriptId: string) => `${API_BASE_URL}/api/beta-applications/manuscript/${manuscriptId}`,
    updateStatus: (id: string) => `${API_BASE_URL}/api/beta-applications/${id}/status`,
    withdraw: (id: string) => `${API_BASE_URL}/api/beta-applications/${id}/withdraw`,
  },
  queries: {
    submit: `${API_BASE_URL}/api/queries`,
    myQueries: `${API_BASE_URL}/api/queries/my-queries`,
    inbox: `${API_BASE_URL}/api/queries/inbox`,
    detail: (id: string) => `${API_BASE_URL}/api/queries/${id}`,
    updateStatus: (id: string) => `${API_BASE_URL}/api/queries/${id}/status`,
  },
  feedback: {
    submit: `${API_BASE_URL}/api/feedback`,
    forManuscript: (manuscriptId: string) => `${API_BASE_URL}/api/feedback/manuscript/${manuscriptId}`,
    activeReaders: (manuscriptId: string) => `${API_BASE_URL}/api/feedback/active-readers/${manuscriptId}`,
  },
  agents: {
    list: `${API_BASE_URL}/api/agents`,
    detail: (id: string) => `${API_BASE_URL}/api/agents/${id}`,
  },
  readers: {
    byId: (id: string) => `${API_BASE_URL}/api/readers/${id}`,
  },
};

/**
 * Helper function to get auth headers with JWT token
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

/**
 * Helper function to make authenticated API calls
 * This is modified to use the MockAPI instead of fetch
 */
export const apiCall = async (url: string, options: RequestInit = {}) => {
  // Parse the URL to determine which mock API method to call
  const urlObj = new URL(url, 'http://demo.local');
  const path = urlObj.pathname;
  const method = options.method || 'GET';
  const body = options.body ? JSON.parse(options.body as string) : undefined;

  // Parse query parameters
  const params: Record<string, string> = {};
  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  try {
    // Auth endpoints
    if (path === '/api/auth/login' && method === 'POST') {
      return await MockAPI.login(body.email, body.password);
    }
    if (path === '/api/auth/logout' && method === 'POST') {
      return await MockAPI.logout();
    }
    if (path === '/api/auth/me' && method === 'GET') {
      return await MockAPI.me();
    }

    // Manuscript endpoints
    if (path === '/api/manuscripts' && method === 'GET') {
      // Convert query params to appropriate types
      const manuscriptParams: any = {};
      if (params.status) manuscriptParams.status = params.status;
      if (params.genre) manuscriptParams.genre = params.genre;
      if (params.seekingBeta) manuscriptParams.seekingBeta = params.seekingBeta === 'true';
      return await MockAPI.getManuscripts(manuscriptParams);
    }
    if (path === '/api/manuscripts' && method === 'POST') {
      return await MockAPI.createManuscript(body);
    }
    if (path.match(/^\/api\/manuscripts\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getManuscriptById(id);
    }
    if (path.match(/^\/api\/manuscripts\/[^/]+$/) && method === 'PUT') {
      const id = path.split('/').pop()!;
      return await MockAPI.updateManuscript(id, body);
    }
    if (path.match(/^\/api\/manuscripts\/[^/]+$/) && method === 'DELETE') {
      const id = path.split('/').pop()!;
      return await MockAPI.deleteManuscript(id);
    }

    // Beta application endpoints
    if (path === '/api/beta-applications' && method === 'POST') {
      return await MockAPI.submitApplication(body);
    }
    if (path === '/api/beta-applications/my-applications' && method === 'GET') {
      return await MockAPI.getMyApplications();
    }
    if (path === '/api/beta-applications/my-active-reads' && method === 'GET') {
      return await MockAPI.getMyActiveReads();
    }
    if (path === '/api/beta-applications/all-applications' && method === 'GET') {
      return await MockAPI.getAllApplicationsForWriter();
    }
    if (path.match(/^\/api\/beta-applications\/manuscript\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getApplicationsForManuscript(id);
    }
    if (path.match(/^\/api\/beta-applications\/[^/]+\/status$/) && (method === 'PUT' || method === 'PATCH')) {
      const id = path.split('/')[3];
      return await MockAPI.updateApplicationStatus(id, body.status);
    }
    if (path.match(/^\/api\/feedback\/active-readers\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getActiveReadersForManuscript(id);
    }

    // Query endpoints
    if (path === '/api/queries' && method === 'POST') {
      return await MockAPI.submitQuery(body);
    }
    if (path === '/api/queries/my-queries' && method === 'GET') {
      return await MockAPI.getMyQueries();
    }
    if (path === '/api/queries/inbox' && method === 'GET') {
      return await MockAPI.getQueryInbox();
    }
    if (path.match(/^\/api\/queries\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getQueryById(id);
    }
    if (path.match(/^\/api\/queries\/[^/]+\/status$/) && method === 'PUT') {
      const id = path.split('/')[3];
      return await MockAPI.updateQueryStatus(id, body.status, body.response);
    }

    // Feedback endpoints
    if (path === '/api/feedback' && method === 'POST') {
      return await MockAPI.submitFeedback(body);
    }
    if (path.match(/^\/api\/feedback\/manuscript\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getFeedbackForManuscript(id);
    }

    // Agent endpoints
    if (path === '/api/agents' && method === 'GET') {
      return await MockAPI.getAgents(body);
    }
    if (path.match(/^\/api\/agents\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getAgentById(id);
    }

    // User/Profile endpoints
    if (path === '/api/users/profile' && method === 'GET') {
      return await MockAPI.getProfile();
    }
    if (path === '/api/users/profile' && method === 'PUT') {
      return await MockAPI.updateProfile(body);
    }

    // Reader endpoints
    if (path.match(/^\/api\/readers\/[^/]+$/) && method === 'GET') {
      const id = path.split('/').pop()!;
      return await MockAPI.getReaderById(id);
    }

    // If no match found, return error
    throw {
      status: 404,
      message: `Mock API endpoint not found: ${method} ${path}`,
    };
  } catch (error: any) {
    throw error;
  }
};
